import {
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Directive({ selector: '[appClickOutside]', standalone: true })
export class ClickOutsideDirective implements OnInit, OnDestroy {
  @Output()
  public appClickOutside: EventEmitter<Event> = new EventEmitter<Event>();
  private _nodesExcluded: Array<HTMLElement> = [];
  private _events: Array<string> = ['click'];

  constructor(
    private readonly _elementRef: ElementRef,
    private readonly _ngZone: NgZone,
    @Inject(PLATFORM_ID)
    private readonly _platformId: string,
  ) {
    this._initOnClickBody = this._initOnClickBody.bind(this);
    this._onClickBody = this._onClickBody.bind(this);
    this._onWindowBlur = this._onWindowBlur.bind(this);
  }

  private _clickOutsideEvents = '';

  get clickOutsideEvents(): string {
    return this._clickOutsideEvents;
  }

  @Input()
  set clickOutsideEvents(value: string) {
    this._clickOutsideEvents = value;
  }

  private _excludeBeforeClick = false;

  get excludeBeforeClick(): boolean {
    return this._excludeBeforeClick;
  }

  @Input()
  set excludeBeforeClick(value: boolean) {
    this._excludeBeforeClick = value;
  }

  private _exclude = '';

  get exclude(): string {
    return this._exclude;
  }

  @Input()
  set exclude(value: string) {
    this._exclude = value;
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }
    this._init();
  }

  private _emitOnBlur = false;

  get emitOnBlur(): boolean {
    return this._emitOnBlur;
  }

  @Input()
  set emitOnBlur(value: boolean) {
    this._emitOnBlur = value;
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }
    this._init();
  }

  @Input()
  private _delayClickOutsideInit = false;

  get delayClickOutsideInit(): boolean {
    return this._delayClickOutsideInit;
  }

  @Input()
  set delayClickOutsideInit(value: boolean) {
    this._delayClickOutsideInit = value;
  }

  private _attachOutsideOnClick = false;

  get attachOutsideOnClick(): boolean {
    return this._attachOutsideOnClick;
  }

  @Input()
  set attachOutsideOnClick(value: boolean) {
    this._attachOutsideOnClick = value;
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }
    this._init();
  }

  private _clickOutsideEnabled = true;

  get clickOutsideEnabled(): boolean {
    return this._clickOutsideEnabled;
  }

  @Input()
  set clickOutsideEnabled(value: boolean) {
    this._clickOutsideEnabled = value;
  }

  public ngOnInit(): void {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }

    this._init();
  }

  public ngOnDestroy(): void {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }
    this._removeClickOutsideListener();
    this._removeAttachOutsideOnClickListener();
    this._removeWindowBlurListener();
  }

  private _init() {
    if (this._clickOutsideEvents !== '') {
      this._events = this._clickOutsideEvents.split(',').map((e) => e.trim());
    }

    this._excludeCheck();

    if (this._attachOutsideOnClick) {
      this._initAttachOutsideOnClickListener();
    } else {
      this._initOnClickBody();
    }

    if (this._emitOnBlur) {
      this._initWindowBlurListener();
    }
  }

  private _initOnClickBody() {
    if (this._delayClickOutsideInit) {
      setTimeout(this._initClickOutsideListener.bind(this));
    } else {
      this._initClickOutsideListener();
    }
  }

  private _excludeCheck() {
    if (this._exclude) {
      try {
        const nodes = Array.from(document.querySelectorAll(this._exclude)) as Array<HTMLElement>;
        if (nodes) {
          this._nodesExcluded = nodes;
        }
      } catch (err) {
        console.error('[ng-click-outside] Check your exclude selector syntax.', err);
      }
    }
  }

  private _onClickBody(ev: Event) {
    if (!this._clickOutsideEnabled) {
      return;
    }

    if (this._excludeBeforeClick) {
      this._excludeCheck();
    }

    if (!this._elementRef.nativeElement.contains(ev.target) && !this._shouldExclude(ev.target)) {
      this._emit(ev);

      if (this._attachOutsideOnClick) {
        this._removeClickOutsideListener();
      }
    }
  }

  /**
   * Resolves problem with outside click on iframe
   * @see https://github.com/arkon/ng-click-outside/issues/32
   */
  private _onWindowBlur(ev: Event) {
    setTimeout(() => {
      if (!document.hidden) {
        this._emit(ev);
      }
    });
  }

  private _emit(ev: Event) {
    if (!this._clickOutsideEnabled) {
      return;
    }

    this._ngZone.run(() => this.appClickOutside.emit(ev));
  }

  private _shouldExclude(target: any): boolean {
    for (const excludedNode of this._nodesExcluded) {
      if (excludedNode.contains(target)) {
        return true;
      }
    }

    return false;
  }

  private _initClickOutsideListener() {
    this._ngZone.runOutsideAngular(() => {
      this._events.forEach((e) => document.addEventListener(e, this._onClickBody));
    });
  }

  private _removeClickOutsideListener() {
    this._ngZone.runOutsideAngular(() => {
      this._events.forEach((e) => document.removeEventListener(e, this._onClickBody));
    });
  }

  private _initAttachOutsideOnClickListener() {
    this._ngZone.runOutsideAngular(() => {
      this._events.forEach((e) => this._elementRef.nativeElement.addEventListener(e, this._initOnClickBody));
    });
  }

  private _removeAttachOutsideOnClickListener() {
    this._ngZone.runOutsideAngular(() => {
      this._events.forEach((e) => this._elementRef.nativeElement.removeEventListener(e, this._initOnClickBody));
    });
  }

  private _initWindowBlurListener() {
    this._ngZone.runOutsideAngular(() => {
      window.addEventListener('blur', this._onWindowBlur);
    });
  }

  private _removeWindowBlurListener() {
    this._ngZone.runOutsideAngular(() => {
      window.removeEventListener('blur', this._onWindowBlur);
    });
  }
}
